import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, Camera, Home, Eye, EyeOff, ChevronDown } from 'lucide-react';
import { LogoHorizontal } from '../components/Logo';
import { PrimaryButton } from '../components/Button';
import { FormHeader } from '../components/Card';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function CompleteProfile() {
    const navigate = useNavigate();
    const { user, config, refreshMe } = useAuth();
    const [name, setName] = useState('');
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [birthdate, setBirthdate] = useState('');
    const [profession, setProfession] = useState('');
    const [professionPrivate, setProfessionPrivate] = useState(false);
    const [country, setCountry] = useState('');
    const [familyCode, setFamilyCode] = useState('');
    const [showCode, setShowCode] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (user?.name) setName(user.name);
        if (user?.profile?.photo_url) setPhotoPreview(user.profile.photo_url);
    }, [user]);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setPhotoPreview(reader.result);
            setPhotoUrl(reader.result); // For MVP: base64. Will move to Cloudinary later.
        };
        reader.readAsDataURL(file);
    };

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const { data } = await api.post('/profile/complete', {
                name: name || undefined,
                photo_url: photoUrl || undefined,
                birthdate: birthdate || undefined,
                profession: profession || undefined,
                profession_private: professionPrivate,
                country: country || undefined,
                family_code: familyCode.trim() ? familyCode.trim().toUpperCase() : undefined,
            });
            await refreshMe();
            if (data.joined_family) {
                navigate('/home', { replace: true });
            } else if (!data.user?.family_id) {
                navigate('/setup-family', { replace: true });
            } else {
                navigate('/home', { replace: true });
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    const skip = async () => {
        // Allow skipping but still need to go to family setup if no family
        if (user?.family_id) navigate('/home', { replace: true });
        else navigate('/setup-family', { replace: true });
    };

    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
            <div className="flex justify-center mb-6"><LogoHorizontal size="lg" /></div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up">
                <FormHeader title="Completa tu perfil" subtitle="Esta información ayudará a tu familia a identificarte." />

                <form onSubmit={submit} className="space-y-4">
                    {/* Avatar */}
                    <div className="flex flex-col items-center">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="w-24 h-24 bg-blue-100 hover:bg-blue-200 rounded-full flex items-center justify-center mb-2 overflow-hidden transition"
                            data-testid="profile-photo-btn"
                        >
                            {photoPreview ? (
                                <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="relative">
                                    <Camera className="w-9 h-9 text-blue-500" />
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold">+</span>
                                </div>
                            )}
                        </button>
                        <span className="text-sm text-gray-500 font-medium">Añade tu foto de perfil</span>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-photo-input" />
                    </div>

                    {/* Name (we use this since the design didn't have it but it's needed) */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1.5">Nombre completo</label>
                        <input
                            type="text"
                            placeholder="Ej. María García"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full h-14 bg-white border border-gray-200 rounded-2xl px-4 text-base text-gray-900 placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20"
                            data-testid="profile-name-input"
                        />
                    </div>

                    <FieldWithIcon label="Fecha de nacimiento" icon={Calendar} type="date" value={birthdate} onChange={setBirthdate} testId="profile-birthdate-input" />

                    <FieldWithPrivacy label="Profesión" icon={Briefcase} placeholder="Ej. Ingeniero, Estudiante, etc." value={profession} onChange={setProfession} isPrivate={professionPrivate} onTogglePrivate={() => setProfessionPrivate((p) => !p)} testId="profile-profession-input" />

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1.5">País</label>
                        <div className="relative">
                            <select
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="w-full h-14 bg-white border border-gray-200 rounded-2xl pl-4 pr-12 text-base text-gray-900 appearance-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                                data-testid="profile-country-select"
                            >
                                <option value="">Selecciona...</option>
                                <option value="GT">Guatemala</option>
                                <option value="MX">México</option>
                                <option value="ES">España</option>
                                <option value="US">Estados Unidos</option>
                                <option value="AR">Argentina</option>
                                <option value="CO">Colombia</option>
                                <option value="CL">Chile</option>
                                <option value="PE">Perú</option>
                                <option value="VE">Venezuela</option>
                                <option value="HN">Honduras</option>
                                <option value="SV">El Salvador</option>
                                <option value="NI">Nicaragua</option>
                                <option value="CR">Costa Rica</option>
                                <option value="PA">Panamá</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Family code optional */}
                    <button
                        type="button"
                        onClick={() => setShowCode((s) => !s)}
                        className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition"
                        data-testid="profile-family-code-toggle"
                    >
                        <Home className="w-5 h-5 text-gray-500" />
                        <span className="text-sm text-gray-700 font-medium">¿Ya tienes un código de familia?</span>
                    </button>

                    {showCode && (
                        <input
                            type="text"
                            value={familyCode}
                            onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                            placeholder="FAM-XXXX-XXXX"
                            className="w-full h-12 bg-white border border-gray-200 rounded-2xl px-4 text-center tracking-wider focus:border-brand focus:ring-2 focus:ring-brand/20"
                            data-testid="profile-family-code-input"
                        />
                    )}

                    {error && <div className="text-red-500 text-sm font-medium" data-testid="profile-error">{error}</div>}

                    <PrimaryButton type="submit" loading={loading} testId="profile-submit-btn">Continuar</PrimaryButton>
                </form>

                <button onClick={skip} className="block mx-auto mt-4 text-gray-500 text-sm hover:text-gray-700 font-medium" data-testid="profile-skip-btn">
                    Completar después
                </button>
            </div>
        </div>
    );
}

function FieldWithIcon({ label, icon: Icon, type, value, onChange, placeholder, testId }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">{label}</label>
            <div className="relative">
                <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full h-14 bg-white border border-gray-200 rounded-2xl pl-12 pr-4 text-base text-gray-900 placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20"
                    data-testid={testId}
                />
            </div>
        </div>
    );
}

function FieldWithPrivacy({ label, icon: Icon, value, onChange, placeholder, isPrivate, onTogglePrivate, testId }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">{label}</label>
            <div className="relative">
                <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full h-14 bg-white border border-gray-200 rounded-2xl pl-12 pr-12 text-base text-gray-900 placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20"
                    data-testid={testId}
                />
                <button
                    type="button"
                    onClick={onTogglePrivate}
                    title={isPrivate ? 'Privado — solo tú' : 'Visible para la familia'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand"
                    data-testid={`${testId}-privacy-toggle`}
                >
                    {isPrivate ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
            </div>
        </div>
    );
}
